import React, { Component, useEffect, useState } from "react";
import { connect } from "react-redux";
import { registerClick } from "../actions/application_actions";
import { createCookie, readCookie } from "../helpers/cookie_helper";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import LoanSelector from "./LoanSelector_container";
import { withRouter } from "react-router";
import { ButtonSize, ButtonVariant, InterfaceButton } from "./ui/button";

export interface CreditBuilderProps {
  utm: any,
  registerClick: Function
}

function CreditBuilder(props : CreditBuilderProps) {
  const [openedApplication, setOpenedApplication] = useState(false);

  useEffect(() => {
    if (props.utm.source === "remitly") {
      let cookie = readCookie("credit-builder-remitly");
      let type;
      if (cookie) {
        type = "cb-remitly_r";
      } else {
        type = "cb-remitly";
        cookie = (new Date()).toString();
        createCookie("credit-builder-remitly", cookie);
      }

      props.registerClick(null, null, type, cookie);
    }
  }, []);

  // Jumps back to the top of the page and resets the application button; used for CTA at the bottom.
  // Note that this must return null in order for it to be set as the onClick for a button.
  const jumpToApplication = () : null => {
    setOpenedApplication(false);
    window.scrollTo(0, 0);
    return null
  }

  return (
    <article className="article-main">
      <Helmet>
        <title>Credit Builder Installment Loans | Fig Loans</title>
        <link
          rel="canonical"
          href="https://www.figloans.com/credit-builder"
        />
        <meta
          name="description"
          content="Looking for the best credit builder loans online? Fig Loans offers low-cost credit builder loans to help you build or rebuild your credit, even with bad credit. Apply now!"
        />
        <meta
          property="og:description"
          content="Looking for the best credit builder loans online? Fig Loans offers low-cost credit builder loans to help you build or rebuild your credit, even with bad credit. Apply now!"
        />
      </Helmet>
      <section
        className="section-panel bg-grey section-welcome"
        id="cb-welcome"
      >
        <div className="content-container">
          <div className="content group">
            <h1>Credit Builder Loans: Build Your Credit Faster</h1>
            <div className="column left">
              <p className="large">
                Are you looking for the best way to build credit or rebuild your credit effectively?
                Fig Loans offers a powerful, low-cost <b>credit builder loan</b> online designed to help you
                establish a strong financial foundation. Our credit builder loan is specifically crafted
                for individuals who may have <b>bad credit or no credit history</b> but are committed to improving
                their financial future. Before you decide, keep reading to learn more about the credit builder
                loans generally and Fig Loans' specifically.
              </p>
            </div>

            <aside className="aside-loan-calculator">
              <div className="content">
                <h3>Begin building your credit history today!</h3>
                <div style={{ display: "block" }}>
                  <p>
                    <b>NOTE:</b>{" "}
                    A credit builder loan is a reverse loan, meaning that you make regular payments,
                    but your loan funds are held in a savings account and disbursed at the <i>end</i> of the loan.{" "}
                    <b>If you are looking for funds immediately, please instead apply for a <Link to="/">traditional loan</Link>.</b>
                  </p>
                  
                  {openedApplication ? (
                    <LoanSelector 
                      edit={false} 
                      product={"credit_builder"}
                      entryPoint="credit_builder"
                      flowContext="new_application"
                    />
                  ) : (
                    <>
                      <p>
                        For more information, read below.
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <InterfaceButton
                          onClick={() => {setOpenedApplication(true)}}
                          variant={ButtonVariant.PRIMARY}
                          size={ButtonSize.FULL_WIDTH}
                          id="cb_submit"
                        >
                          Apply Now
                        </InterfaceButton>
                      </div>
                    </>
                  )}

                  <div className="clear" />
                </div>
              </div>
            </aside>
          </div>
          <div className="compare">
            <div className="content group">
              <p>See how it works...</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-panel bg-white" id="section-features">
        <img
          loading="lazy"
          src="https://cdn.figloans.com/home/arrow-99w.avif"
          alt="compare"
          className="arrow"
          height={99}
        />
      </section>

      <div className="container">
        <div className="panel-white">
          <div className="row">
            <div className="glance">
              <h2>
                What is a Credit Builder Loan?
              </h2>
              <p>
                A credit builder loan is a unique financial product designed to help you establish or improve your credit score without needing an existing credit history.
              </p>
              <section>
                <h3>
                  How a Credit Building Loan Works
                </h3>
                <p>
                  When a credit builder is approved, loan funds are not disbursed to you, instead they are
                  sent to a savings account to secure your loan. You make affordable, fixed payments and every
                  single payment you make is reported to all three major credit bureaus. When you complete paying
                  off the loan, the held funds are sent to you. Because your loan funds are returned at the end,
                  you're effectively saving while building credit!
                </p>
              </section>
            </div>
          </div>
        </div>

        <div className="panel-grey">
          <div className="row">
            <div className="overtime">
              <h2>
              Understanding Your Credit: Scores, History & How it Works
            </h2>
            <p>
              To fully appreciate the impact of a credit builder loan, it's helpful to understand the
              fundamentals of your credit profile and how your credit score works.
            </p>
            
            <section>
              <h3>
                What is a Credit Score and Why Does it Matter?
              </h3>
              <p>
                Your credit score is a three-digit number, typically ranging from 300 to 850, that lenders use
                to assess your creditworthiness. It's like your financial GPA. A higher credit score signals to
                lenders that you're a responsible borrower, making it easier to get approved for loans, credit cards,
                apartments, and even lower insurance rates. It directly impacts your ability to secure favorable terms,
                which is why actively building and improving your credit score is so important for your overall financial
                health.
              </p>
            </section>

            <section>
              <h3>
                How Payment History Affects Your Credit (and Student Loans)
              </h3>

              <p>
                Your payment history is the single most important factor in your credit score, accounting for 35% of your
                FICO® Score. Payment history includes how consistently you make on-time payments on all your credit accounts,
                including student loans, credit cards, and installment loans like those from Fig. Every time you make an on-time
                payment, it builds a positive record. Conversely, missed or late payments can significantly damage your score. A
                credit builder loan gives you a dedicated way to create a consistent, positive payment history when you don't need
                cash today.
              </p>
            </section>

            <div className="image-container">
              <img src="https://cdn.figloans.com/informational/credit_journey/full.avif"
                style={{ width: "100%", aspectRatio: 800 / 300 }}
                alt="Credit Score with Installment Loans" />

                <p className="disclaimer">
                  *This graph is for illustrative purposes only. Using a credit builder loan responsibly can help improve your credit
                  score over time, but improvement is not guaranteed. Individual results may vary based on how credit bureaus evaluate
                  your personal credit history, payment behavior, and other financial factors, especially those beyond this loan.
                </p>
            </div>

            <section>
              <h3>
                The Importance of Your Credit Mix and Credit Length
              </h3>

              <p>
                Beyond payment history, other factors contribute to your credit score. Your credit mix (10% of your FICO® Score)
                refers to having different types of credit, such as both installment loans (like a mortgage or a credit builder loan)
                and revolving credit (like credit cards). Demonstrating you can manage multiple types of credit responsibly is a plus.
                Your length of credit history (15% of your FICO® Score) measures how long your accounts have been open. The longer your
                history of responsible use, the better. A credit builder loan can help you establish a positive payment history and
                contribute to a good credit mix. And if your prior loans were short-term, a credit builder loan can even help with your
                length of credit history component.
              </p>
            </section>

            <section>
              <h3>
                Secured Credit Cards and Authorized Users
              </h3>
              <p>
                Secured credit cards may require an upfront cash deposit that acts as your credit limit. They can be a good way to build
                credit as your payments are reported. Being an authorized user on someone else's credit card can also help if the primary
                account holder has good payment habits, but you don't have direct control or responsibility, making it less impactful than
                having your own account. A credit builder loan offers an independent way to establish your own strong credit profile through
                consistent on-time payments.
              </p>
            </section>
            </div>
          </div>
        </div>
      </div>

      <div className="panel-white">
        <div className="row">
          <div className="glance">
            <section>
              <h2>
                Credit Builder vs. Traditional Loan: What's the Difference?
              </h2>

              <p>
                While both credit builder loans and traditional loans involve borrowing money, their primary purposes and structures differ
                significantly, especially when your goal is to build credit or improve your credit score.
              </p>
            </section>

            <div className="split">
              <div className="quarter">
                <div className="image-container" style={{ maxWidth: 250 }}>
                    <img src="https://cdn.figloans.com/credit_builder/credit_builder_flow.png" style={{ width: "100%", aspectRatio: 1 }} />
                  </div>
              </div>

              <div className="three-quarters right">
                <h3>
                  How a Credit Builder Loan Differs
                </h3>

                <p>
                  The core difference lies in their focus: a credit builder loan is primarily designed to help you establish or improve your
                  credit score, not to provide immediate funds. Similar to other credit builder loans, the Fig Credit Builder Loan holds funds
                  in a secure account until you've paid the credit builder off. Since you are not receiving immediate funds, the emphasis is on
                  your on-time payments being reported to all major credit bureaus. Compared to traditional loans, this makes credit builders
                  highly accessible and much cheaper for those with bad credit or no credit history. A credit builder is a great way for you to
                  prioritize your journey to financial health even when you don’t need access to immediate funds.
                </p>
              </div>
            </div>

            <div className="split">
              <div className="quarter">
                <div className="image-container" style={{ maxWidth: 250 }}>
                  <img src="https://cdn.figloans.com/credit_builder/traditional_flow.png" style={{ width: "100%", aspectRatio: 1 }}/>
                </div>
              </div>

              <div className="three-quarters right">
                <h3>
                  Key Characteristics of a Traditional Loan
                </h3>
                <p>
                  A traditional loan, such as a personal loan, auto loan, or mortgage, typically provides you with the full loan amount upfront
                  for a specific purpose (like buying a car or consolidating debt). Lenders for these loans primarily assess your existing credit
                  score, credit history, and income to determine eligibility, interest rates, and loan terms. While on-time payments on a traditional
                  loan do contribute positively to your credit, their main function isn't explicitly credit building. They are generally less accessible
                  and more expensive if you have no credit history or are working to repair bad credit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="panel-grey">
        <div className="row">
          <div className="overtime">
            <h2>
              Why Choose a Fig Loans Credit Builder Loan?
            </h2>
            <p>
              Choosing Fig Loans for your credit builder loan means partnering with a lender committed to your financial health. We offer an
              effective approach designed for simplicity and maximum impact on your credit.
            </p>

            <section>
              <h3>
                Low-Cost Path to a Stronger Credit Score
              </h3>
              <p>
                At Fig Loans, we believe building credit shouldn't be a financial burden. Our credit builder loans are structured to be affordable,
                making it easier for you to make on-time payments consistently. Our credit builder includes a late payment protection feature. This
                helps ensure your journey to a stronger credit score is clear, predictable, and cost-effective, allowing you to focus on your financial
                improvement without unexpected expenses.
              </p>
            </section>

            <section>
              <h3>
                Simple Application Process
              </h3>
              <p>
                Applying for a Fig Credit Builder Loan is straightforward and can be completed entirely online, often taking just a few minutes. Our
                loan agreements show you exactly what your payments are, when they're due, and how your loan helps build credit, with all terms clearly
                outlined from the start. This simple approach removes complexities sometimes associated with applying for loans.
              </p>
            </section>

            <section>
              <h3>
                Reported to All Major Credit Bureaus (TransUnion, Equifax, Experian)
              </h3>
              <p>
                The power of a Fig Credit Builder Loan lies in its reporting. Every single payment you make is consistently reported to all three major
                credit bureaus: TransUnion, Equifax, and Experian. This comprehensive reporting is vital because these institutions are the largest
                compilers of your credit report and provide the information used to calculate most credit scores. Regular positive reporting actively
                helps you establish a strong payment history.
              </p>
            </section>
          </div>
        </div>
      </div>
      
      <div className="panel-white">
        <div className="row">
          <div className="glance">
            <h2>
              Who is a Fig Loans Credit Builder Loan For?
            </h2>
            <p>
              A Fig Credit Builder Loan is a versatile tool, specifically designed to help a wide range of individuals take control of their credit profile.
            </p>

            <section>
              <h3>
                Ideal for Those with No Credit History (Credit Invisible & Unscorable)
              </h3>
              <p>
                If you're just starting your financial journey, perhaps as a young adult, a recent immigrant, or simply someone who has avoided credit,
                you might be 'credit invisible' or 'unscorable'. This means you lack the credit history lenders need to assess your risk. A Fig Credit
                Builder Loan is an ideal solution. It helps you responsibly establish your first positive tradeline on your credit report, giving you the
                foundation needed to gain access to future financial products and services.
              </p>
            </section>

            <section>
              <h3>
                An Effective Solution for Bad Credit
              </h3>
              <p>
                Past financial challenges, such as missed payments, defaults, or bankruptcies, can leave you with bad credit or a low credit score. This
                can make it incredibly difficult to get approved for loans or even housing. A Fig Credit Builder Loan offers an effective pathway to rebuild.
                By consistently making on-time payments, you can gradually outweigh negative marks with positive ones, demonstrating renewed financial
                responsibility and actively working to improve your credit score over time.
              </p>
            </section>

            <section>
              <h3>
                Overcoming Challenges to Build First-Time Credit
              </h3>
              <p>
                Beyond traditional categories, many people face unique circumstances that prevent them from easily building first-time credit. This could
                include relying solely on cash, being an authorized user but not having primary accounts, or simply not knowing where to start. Fig Credit
                Builder Loans provide a structured and supportive environment to initiate your credit journey, guiding you through the process of establishing
                a reliable payment history that sets the stage for future financial independence.
              </p>
            </section>

            <section>
              <h3>
                Student Loans and Other Installment Loans
              </h3>
              <p>
                While student loans and other major installment loans (like auto loans or mortgages) can help build credit when paid on time, they might not
                always be accessible or ideal for someone specifically trying to establish initial credit or repair bad credit. Fig Credit Builder Loans serve
                as a more targeted and often more accessible entry point. They allow you to demonstrate consistent payment behavior on a smaller, manageable
                installment loan, specifically designed for the purpose of credit building, without the large financial commitment or stringent credit requirements
                of other loan types.
              </p>
            </section>
          </div>
        </div>
      </div>

      <div className="panel-grey" id="frequently-asked-questions">
        <div className="row">
          <div className="overtime">
            <h2>
              Frequently Asked Questions
            </h2>

            <p>
              Here are answers to common questions about Fig Credit Builder Loans and how they can help you improve your credit score:
            </p>

            <div className="faq-links">
              <ul>
                <li>
                  <a href="#faq-how-much">How much can I borrow with a Credit Builder Loan?</a>
                </li>
                <li>
                  <a href="#do-i-get-money">Do I get money upfront from a Credit Builder Loan?</a>
                </li>
                <li>
                  <a href="#associated-fees">What are the fees associated with a Credit Builder Loan?</a>
                </li>
                <li>
                  <a href="#monthly-payments">How do I make my monthly payments?</a>
                </li>
                <li>
                  <a href="#score-increase">How much will my credit score increase?</a>
                </li>
                <li>
                  <a href="#close-early">Can I close my loan early?</a>
                </li>
                <li>
                  <a href="#late-fees">Are there late fees?</a>
                </li>
                <li>
                  <a href="#third-party">Why do you give my funds to a 3rd party bank?</a>
                </li>
                <li>
                  <a href="#credit-building-loans">What kind of loans help build credit?</a>
                </li>
                <li>
                  <a href="#with-bad-credit">Can I get a Credit Builder Loan with bad credit?</a>
                </li>
                <li>
                  <a href="#can-i-be-denied">Can you be denied for a Credit Builder Loan?</a>
                </li>
              </ul>
            </div>

            <section>
              <h3 id="faq-how-much">
                How much can I borrow with a Credit Builder Loan?
              </h3>

              <p>
                With a Fig Credit Builder Loan, the amount you can borrow ranges from $500 to $1,000. These amounts are designed to be manageable, allowing
                you to easily make on-time payments that build your credit, without overextending your finances. Our goal is to provide a sensible path to
                improve your credit score.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="do-i-get-money">
                Do I get money upfront from a Credit Builder Loan?
              </h3>
              <p>
                Fig Credit Builder Loans are specifically designed to help you continue building credit when you don’t need access to immediate funds. Holding
                your loan funds in an escrow account allows  you to pay a lower interest rate than if you get the money upfront.  If you need money today, you
                should consider applying for a traditional Fig Loan instead, which also builds your credit history by reporting your payments to all three major
                credit bureaus.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="associated-fees">
                What are the fees associated with a Credit Builder Loan?
              </h3>
              <p>
                The cost of your Fig Credit Builder Loan will be clearly outlined in your loan agreement, typically comprising the principal amount plus a finance
                charge and a rate of interest. We believe in straightforward terms to help you build credit without unexpected costs.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="monthly-payments">
                How do I make my monthly payments?
              </h3>
              <p>
                Making your monthly payments for a Fig Credit Builder Loan is simple and convenient. We offer flexible options, including automatic payments directly
                from your bank account or payments via debit card. Our aim is to make on-time payments as easy as possible, ensuring you consistently build a positive
                payment history to improve your credit score.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="score-increase">
                How much will my credit score increase?
              </h3>
              <p>
                The amount your credit score could increase with a Fig Credit Builder Loan varies for each individual, depending on your starting score, existing credit
                profile, other credit products you may obtain, and consistent on-time payments. In a 2020{" "}
                <Link to="https://www.consumerfinance.gov/about-us/newsroom/cfpb-study-shows-financial-product-could-help-consumers-build-credit/">CFPB study</Link>{" "}
                of credit builder loans, "participants without existing debt saw their credit scores increase by 60 points more than participants with existing debt."
                Your credit score could increase by a significant amount if you are using a credit builder loan to establish initial credit history.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="close-early">
                Can I close my loan early?
              </h3>
              <p>
                Yes, you absolutely can close your Fig Credit Builder Loan early without any penalty. We believe in providing flexibility while you build credit. While
                paying off loans early is a financially smart move, it's worth noting that the longest, most consistent payment history typically yields the greatest
                long-term credit score improvement. However, the choice is always yours, and there are no prepayment fees with Fig.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="late-fees">
                Are there late fees?
              </h3>
              <p>
                No, Fig Loans does not charge late fees for our Credit Builder Loans. We understand that life happens, and sometimes you might need flexibility. If
                you anticipate difficulty making a payment, simply contact us. We're here to work with you to ensure you can maintain your on-time payment record and
                continue to build your credit score without unnecessary penalties.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="third-party">
                Why do you give my funds to a 3rd party bank?
              </h3>
              <p>
                Fig Loans works with third-party banking partners to ensure secure processing of your Fig Credit Builder Loan funds. This collaboration allows us to
                focus on what we do best: providing accessible installment loans and dedicated support to help you build credit and improve your credit score through
                consistent reporting to all major credit bureaus. This is a standard and secure practice in the financial industry.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="credit-building-loans">
                What kind of loans help build credit?
              </h3>
              <p>
                Several types of loans can help build credit when managed responsibly:
              </p>
              <ul>
                <li>
                  <b>Credit Builder Loans:</b> Like those from Fig, these are specifically designed to help you establish or re-establish credit by reporting payments
                  to credit bureaus.
                </li>
                <li>
                  <b>Secured Credit Cards:</b> These require a deposit, acting as your credit limit, and report your payment activity.
                </li>
                <li>
                  <b>Student Loans:</b> While not designed for credit building, consistent on-time payments on student loans can significantly contribute to your
                  credit history.
                </li>
                <li>
                  <b>Auto Loans & Mortgages:</b> These are large installment loans that, when paid responsibly, can build substantial positive credit history.
                </li>
              </ul>
              <p>
                The key across all these loans is making on-time payments consistently, as this is the primary factor impacting your credit score.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="with-bad-credit">
                Can I get a Credit Builder Loan with bad credit?
              </h3>
              <p>
                Yes, absolutely! Fig Credit Builder Loans are specifically designed to help individuals with bad credit or no credit history establish or improve their
                credit profile. We focus on your current ability to repay and your commitment to improving your financial standing. Our accessible installment loans
                provide a clear path to build credit and improve your credit score, even if you've faced financial challenges in the past.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>

            <section>
              <h3 id="can-i-be-denied">
                Can you be denied for a Credit Builder Loan?
              </h3>
              <p>
                While Fig Credit Builder Loans are designed to be highly accessible, it is possible to be denied. Our goal is to ensure that the loan is a tool for
                your success, not a source of further financial strain. We aim to approve individuals who we believe can successfully make on-time payments and
                therefore build their credit score effectively.
              </p>

              <p>
                <a href="#frequently-asked-questions">back to top</a>
              </p>
            </section>
          </div>
        </div>
      </div>

      <div className="panel-white">
        <div className="row">
          <div className="glance">
            <h2>
              Ready to Start Building Your Credit?
            </h2>

            <p>
              Taking control of your financial future starts with a single step. If you're ready to responsibly build credit and improve your credit score with
              a simple and supportive installment loan, a Fig Credit Builder Loan could be the solution you've been looking for. Start your journey to a stronger
              credit profile today.
            </p>
            <div className="center">
              <InterfaceButton
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.MEDIUM}
                onClick={jumpToApplication}>
                Apply Now
              </InterfaceButton>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

const mapStateToProps = (state) => ({
  utm: state.entities.utm,
});

const mapDispatchToProps = (dispatch) => ({
  registerClick: (user_id, loan_id, click_type, comments) =>
    dispatch(registerClick(user_id, loan_id, click_type, comments)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CreditBuilder));
